import { Dialog, Typography } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { Record } from "react-admin";
import CircularProgress from "@material-ui/core/CircularProgress";
import { EmblaCarousel } from "./EmblaCarousel";
import classnames from "classnames";

interface Props<T extends Record> {
  record?: T;
  getThumbnail: (record: T) => string | Promise<string>;
  getRestCount: (record: T) => number;
  getImages: (record: T) => Array<string> | Promise<Array<string>>;
  isDarkTheme?: boolean;
  className?: string;
  label?: string;
  headerClassName?: string;
}

export const ImagesField = <T extends Record>({
  record,
  getThumbnail,
  getRestCount,
  getImages,
  isDarkTheme,
  className,
}: Props<T>) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<string>();
  const [emblaCarouselOpen, setEmblaCarouselOpen] = useState<boolean>(false);
  const [images, setImages] = useState<Array<string>>();

  useEffect(() => {
    if (!record) {
      return;
    }

    setLoading(true);
    Promise.resolve(getThumbnail(record))
      .then((val) => setThumbnail(val))
      .finally(() => setLoading(false));
  }, [record, getThumbnail]);

  const openEmblaCarousel = useCallback(() => {
    if (!record) {
      return;
    }

    setLoading(true);
    Promise.resolve(getImages(record))
      .then((val) => {
        if (!val || !val.length) return;
        setImages(val);
        setEmblaCarouselOpen(true);
      })
      .finally(() => setLoading(false));
  }, [getImages, record]);

  if (!record) {
    return null;
  }

  if (loading) {
    return (
      <div
        className={
          isDarkTheme
            ? !!className
              ? classnames(className, "image-field", "dark")
              : "image-field dark"
            : !!className
            ? classnames(className, "image-field")
            : "image-field"
        }>
        <CircularProgress />
      </div>
    );
  }

  if (!thumbnail) {
    return null;
  }

  const restCount = getRestCount(record);
  return (
    <>
      <div
        className={
          isDarkTheme
            ? !!className
              ? classnames(className, "image-field", "dark")
              : "image-field dark"
            : !!className
            ? classnames(className, "image-field")
            : "image-field"
        }
        style={{
          backgroundImage: record?.thumbnailPhoto ? `url(${thumbnail})` : "",
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          width: '80px',
          height: '80px'
        }}
        onClick={openEmblaCarousel}>
        {!!restCount && (
          <Typography className="other-photos-count">+{restCount}</Typography>
        )}
      </div>
      <Dialog
        maxWidth="xl"
        open={emblaCarouselOpen}
        onClose={() => setEmblaCarouselOpen(false)}>
        {images ? <EmblaCarousel slides={images} /> : null}
      </Dialog>
    </>
  );
};
